import React, { useState, useEffect } from "react";
import {
  Box, Modal, Typography, Paper, IconButton,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
  FormControl, TextField
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Autocomplete from "@mui/material/Autocomplete";
import PageButtonBlack from "../GeneralComponents/PageButtonBlack";
import FormularioRepresentante from "../GeneralComponents/FormularioRepresentante";
import { validarRut } from "../utils/utilsRut";
import axios from "../../api/axios";

const generarId = () => crypto.randomUUID();

export default function RepresentantesModalAdd({
  open,
  onClose,
  title = "AGREGAR REPRESENTANTE",
  btnText = "Guardar y Agregar",
  onConfirm,
}) {
  /* ---------- estado formulario ------------------------------------ */
  const [rep, setRep] = useState({
    id: generarId(), nombre: "", apellidos: "", rut: "", email: "", telefono: "", isNew: true,
  });

  const [errors, setErrors] = useState({
    rut: false, email: false, nombre: false, apellidos: false,
  });
  const [apiErrorMsg, setApiErrorMsg] = useState("");

  /* ---------- Autocomplete / tabla --------------------------------- */
  const [autoCompleteInput, setAutoCompleteInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [rbdLoading, setRbdLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRBD, setSelectedRBD] = useState("");
  const [eesInfo, setEesInfo] = useState(null);

  /* ---- buscar RBD -------------------------------------------------- */
  useEffect(() => {
    if (autoCompleteInput.trim() === "") {
      setSuggestions([]); setSelectedRBD(""); setEesInfo(null); return;
    }
    let active = true;
    setRbdLoading(true);
    axios.getRBD(autoCompleteInput.trim())
      .then(res => active && setSuggestions(res.data.rbd_list || []))
      .catch(() => active && setSuggestions([]))
      .finally(() => active && setRbdLoading(false));
    return () => { active = false; };
  }, [autoCompleteInput]);

  /* ---- info EES ---------------------------------------------------- */
  const fetchEesInfo = (rbd) => {
  if (!rbd) return;
  axios.getEstadoConvocatoria(rbd)
    .then(res => {
      const row = res.data;
      const estadoNormalizado =
        row.estado === "Activo" || row.estado === "Facturar"
          ? "Alta"
          : row.estado === "NYA"
          ? "NYA"
          : row.estado === "Baja"
          ? "Baja"
          : "Desconocido";

      setEesInfo({
        ...row,
        estado: estadoNormalizado,
      });
    })
  }
  const handleSelectRBD = (e, val) => {
    setSelectedRBD(val || ""); setAutoCompleteInput(val || "");
    setSuggestions([]); setEesInfo(null);
    if (val) fetchEesInfo(val);
  };

  /* ---- VALIDACIONES (idéntico enfoque que PanelAdmin) ------------- */
  const updateErrors = (field, value, forceError = false) => {
    setErrors(prev => {
      const next = { ...prev };
      if (field === "rut") {
        next.rut = forceError || !validarRut(value);
      } else if (field === "email") {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        next.email = !re.test(value);
      } else if (field === "nombre") {
        next.nombre = value.trim() === "";
      } else if (field === "apellidos") {
        next.apellidos = value.trim() === "";
      }
      return next;
    });
  };

  const handleFormChange = (idx, name, value, forceError = false) => {
    setRep(prev => ({ ...prev, [name]: value }));
    updateErrors(name, value, forceError);
  };

  const validarTodos = () =>
    rep.nombre.trim() !== "" &&
    rep.apellidos.trim() !== "" &&
    rep.rut.trim() !== "" &&
    rep.email.trim() !== "" &&
    !errors.rut && !errors.email;

  const guardarEnAPI = async (rbd) => {
    await axios.addRepresentante({
      rbd,
      nombre_representante: rep.nombre,
      rut_representante: rep.rut,
      apellido_representante: rep.apellidos,
      email_representante: rep.email,
      telefono: rep.telefono,
    });
  };

  const confirmar = async () => {
    if (!eesInfo || !validarTodos()) return;
      setLoading(true)
    try {
      await guardarEnAPI(eesInfo.rbd);
      setLoading(false)
      setApiErrorMsg("");
      await onConfirm?.(rep);
      onClose(true, 1);
    } catch (err) {
      setLoading(false)
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Error al guardar representante";
      setApiErrorMsg(msg);
    }
  };

  useEffect(() => {
    if (!open) return;
    setRep({ id: generarId(), nombre:"", apellidos:"", rut:"", email:"", telefono:"", isNew:true });
    setErrors({ rut:false, email:false, nombre:false, apellidos:false });
    setApiErrorMsg("");
    setAutoCompleteInput(""); setSuggestions([]); setSelectedRBD(""); setEesInfo(null);
  }, [open]);

  const disableSave = !eesInfo || !validarTodos();

  /* ---- UI ---------------------------------------------------------- */
  return (
    <Modal open={open} onClose={() => onClose(true, 0)}
      aria-labelledby="modal-titulo"
      sx={{ backdropFilter:"blur(5px)", backgroundColor:"rgba(0,0,0,0.05)" }}
      slotProps={{ backdrop:{ sx:{ opacity:"0.1!important" } } }}
    >
      <Box sx={{
        position:"absolute", width:750, maxWidth:"90vw",
        top:"50%", left:"50%", transform:"translate(-50%, -50%)"
      }}>
        <Paper elevation={3} sx={{ p:3, textAlign:"center" }}>
          <IconButton sx={{ position:"absolute", top:8, right:8 }} onClick={() => onClose(true, 0)}>
            <CloseIcon />
          </IconButton>

          <Typography id="modal-titulo" variant="h3" fontWeight="bold" gutterBottom>
            {title}
          </Typography>

          {/* Autocomplete */}
          <FormControl fullWidth sx={{ mb:2 }}>
            <Autocomplete
              value={selectedRBD}
              onChange={handleSelectRBD}
              inputValue={autoCompleteInput}
              onInputChange={(e,val)=>setAutoCompleteInput(val)}
              options={suggestions}
              loading={rbdLoading}
              loadingText="Cargando..."
              size="small"
              renderInput={(params)=> <TextField {...params} label="RBD" required />}
            />
          </FormControl>

          {/* Tabla */}
          
            <TableContainer sx={{ mb:3, borderRadius:2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{fontWeight:700}}>RBD</TableCell>
                    <TableCell sx={{fontWeight:700}}>Nombre</TableCell>
                    <TableCell sx={{fontWeight:700}}>Operador</TableCell>
                    <TableCell sx={{fontWeight:700}}>Convocatoria</TableCell>
                    <TableCell sx={{fontWeight:700}}>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                {eesInfo && (
                  <TableRow>
                    <TableCell>{eesInfo.rbd}</TableCell>
                    <TableCell>{eesInfo.nombre}</TableCell>
                    <TableCell>{eesInfo.nombre_opeador}</TableCell>
                    <TableCell>{eesInfo.convocatoria}</TableCell>
                    <TableCell>
                      <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "no-wrap",
                                      }}
                                    >
                                      <FiberManualRecordIcon
                                        sx={{
                                          fontSize: "0.75rem",
                                          color:
                                            eesInfo.estado === "Alta"
                                              ? "#41AE43"
                                              : eesInfo.estado === "NYA" ? "#ABABB5" : "#FB3B3B",
                                          marginRight: "0.5rem",
                                        }}
                                      />

                                      
                                      {eesInfo.estado}
                                    </div>
                    </TableCell>
                  </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          

         

          <FormularioRepresentante
            index={0}
            formData={rep}
            errors={errors}
            editableRutNombre
            editableContacto
            onChange={handleFormChange}
          />

          {apiErrorMsg && (
            <Typography color="error" sx={{ mt:1, fontSize:14 }}>
              {apiErrorMsg}
            </Typography>
          )}
          <Box sx={{mt:2}}>
          <PageButtonBlack
            loading={!eesInfo || loading}
            disabled={disableSave}
            onClick={confirmar}
          >
            {btnText}
          </PageButtonBlack></Box>
        </Paper>
      </Box>
    </Modal>
  );
}
